import React, { useState } from 'react';
import Button from '@/components/UI/Button';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import Modal from '@/components/UI/Modal';
import { buildDefaultFamilyLimit, type GovernanceFamilyLimitDraft } from './governanceHelpers';
import type { GovernanceFamilyOption } from './GovernanceFamilyLimitsTable';

interface GovernanceFamilyLimitModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** The row being edited, or null when adding a new family. */
    existing: GovernanceFamilyLimitDraft | null;
    /** Families available to select. */
    families: GovernanceFamilyOption[];
    /** Family ids already present in the draft (used to prevent duplicate rows when adding). */
    usedFamilyIds: number[];
    onSave: (limit: GovernanceFamilyLimitDraft) => void;
}

const firstAvailableFamilyId = (families: GovernanceFamilyOption[], usedFamilyIds: number[]): number =>
    families.find((family) => !usedFamilyIds.includes(family.familyId))?.familyId ?? families[0]?.familyId ?? 0;

const GovernanceFamilyLimitModal: React.FC<GovernanceFamilyLimitModalProps> = ({
    isOpen,
    onClose,
    existing,
    families,
    usedFamilyIds,
    onSave
}) => {
    const initialFamilyId = existing?.familyId ?? firstAvailableFamilyId(families, usedFamilyIds);
    const [draft, setDraft] = useState<GovernanceFamilyLimitDraft>(() => existing ?? buildDefaultFamilyLimit(initialFamilyId));

    // Re-seed the form each time the modal opens for a different row, matching the entity-limit modal pattern.
    const openKey = isOpen ? (existing ? `edit:${existing.familyId}` : `add:${initialFamilyId}`) : null;
    const [seededKey, setSeededKey] = useState<string | null>(null);

    if (openKey !== seededKey) {
        setSeededKey(openKey);

        if (openKey !== null) {
            setDraft(existing ?? buildDefaultFamilyLimit(initialFamilyId));
        }
    }

    const isEditing = existing !== null;

    const familyOptions = families
        .filter((family) => family.familyId === draft.familyId || !usedFamilyIds.includes(family.familyId))
        .map((family) => ({ value: family.familyId, label: family.name }));

    const selectedFamilyName = families.find((family) => family.familyId === draft.familyId)?.name ?? `Family #${draft.familyId}`;

    const handleSave = () => {
        if (draft.familyId <= 0) {
            return;
        }

        onSave(draft);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} title={isEditing ? `Edit limits - ${selectedFamilyName}` : 'Add family limits'} onClose={onClose}>
            <div className="space-y-4">
                <FormSelect
                    disabled={isEditing}
                    helpText="Limits apply across every variant in the selected strategy family."
                    id="governanceFamilyLimitFamily"
                    label="Strategy family"
                    options={familyOptions}
                    value={draft.familyId}
                    onChange={(e) => setDraft({ ...draft, familyId: Number.parseInt(e.target.value, 10) })}
                />

                <FormNumberInput
                    integer
                    helpText="Total open + counted positions allowed across all variants in this family. Blank = no cap."
                    id="governanceFamilyLimitMaxPositions"
                    label="Max positions per family"
                    min={0}
                    placeholder="No cap"
                    value={draft.maxOpenPositionsPerFamily}
                    onValueChange={(value) => setDraft({ ...draft, maxOpenPositionsPerFamily: value })}
                />

                <FormNumberInput
                    helpText="Cap on portfolio risk capital consumed by this strategy family. Blank = no cap."
                    id="governanceFamilyLimitMaxRiskCapitalPercent"
                    label="Max risk capital per family"
                    min={0}
                    placeholder="No cap"
                    step={0.1}
                    suffix="%"
                    value={draft.maxFamilyRiskCapitalPercent}
                    onValueChange={(value) => setDraft({ ...draft, maxFamilyRiskCapitalPercent: value })}
                />

                <div className="space-y-2 border-t border-zinc-800 pt-3">
                    <p className="text-sm text-zinc-400">Which existing exposure counts toward these limits:</p>
                    <FormSwitch
                        checked={draft.includePendingOrders}
                        id="governanceFamilyLimitIncludePending"
                        label="Count unfilled brokerage orders"
                        onChange={(e) => setDraft({ ...draft, includePendingOrders: e.target.checked })}
                    />
                    <FormSwitch
                        checked={draft.includeQueuedTrades}
                        id="governanceFamilyLimitIncludeQueued"
                        label="Count in-flight queued entries"
                        onChange={(e) => setDraft({ ...draft, includeQueuedTrades: e.target.checked })}
                    />
                    <FormSwitch
                        checked={draft.includeManualApprovalPending}
                        id="governanceFamilyLimitIncludeManualApproval"
                        label="Count trades awaiting manual approval"
                        onChange={(e) => setDraft({ ...draft, includeManualApprovalPending: e.target.checked })}
                    />
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={draft.familyId <= 0} variant="primary" onClick={handleSave}>
                        {isEditing ? 'Save limits' : 'Add limits'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default GovernanceFamilyLimitModal;
